import { Injectable, OnDestroy } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxPermissionsService } from 'ngx-permissions';
import { forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
    EFulfillmentOrderAction,
    EFulfillmentOrderStatus,
} from '@app/core/_constant/fulfillment-orders.constants';
import { ShipOrdersComponent } from '@app/modules/shipping/ship-orders/ship-orders.component';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { OrderFulfillmentStatusUpdateComponent } from 'v2/modules/order/components/order-fulfillment-status-update/order-fulfillment-status-update.component';
import { OrderShipFormV2Component } from 'v2/modules/order/components/order-ship-form-v2/order-ship-form-v2.component';
import { OrderShipmentsFormComponent } from 'v2/modules/order/components/order-shipments-form/order-shipments-form.component';
import { IFulfillmentOrder } from 'v2/modules/order/interfaces/order-fulfillment.interface';
import {
    DialogConfig,
    FeatureFlagUtilService,
    LoadingDialogUtilService,
    SnackBarConfig,
} from 'v2/modules/shared';
import { IAgGridDropdownMenuOption } from 'v2/modules/shared/components/ag-grid-dropdown-menu-cell-renderer/ag-grid-dropdown-menu-cell-renderer.component';
import { IGridActionService } from 'v2/modules/shared/interfaces/ag-grid/ag-grid.types';
import { EShipmentFormDataSource } from 'v2/modules/shared/interfaces/order-shipment.interface';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';
import { BaseGridActionService } from 'v2/modules/shared/services/ag-grid/base-grid-action.service';
import { FulfillmentOrderApiService } from 'v2/modules/shared/services/api/fulfillment-order-api.service';
import { OrderPostedApiService } from 'v2/modules/shared/services/api/order/order-posted-api.service';
import {
    UiConfirmationDialogComponent,
    UiFulfillmentActionConfirmationDialogComponent,
} from 'v2/modules/ui';

@Injectable({
  providedIn: 'root',
})
export class FulfillmentOrdersGridActionService
  extends BaseGridActionService
  implements IGridActionService, OnDestroy
{
  private onDestroy$ = new Subject();

  salesOrderRelations :string = [
    'orderItems.orderedUom:id,name',
    'channel.pushOrderIntegrationMapping.appIntegrationAccount',
  ].join('|');

  salesOrder :any = {};
  clearGridStateTrigger$ = new Subject<void>();

  constructor(
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private router :Router,
    private fulfillmentOrdersApiService :FulfillmentOrderApiService,
    featureToggleService :FeatureToggleService,
    permissionsService :NgxPermissionsService,
    private featureFlagUtilService :FeatureFlagUtilService,
    private modal :NgbModal,
    private orderPostedApiService :OrderPostedApiService,
    private loadingDialogService :LoadingDialogUtilService,
    private decimalPipe :DecimalPlacesPipe,
  ) {
    super(featureToggleService, permissionsService);
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  getDropdownActions() :IAgGridDropdownMenuOption[] {
    const defaultActions :IAgGridDropdownMenuOption[] = [
      // Disable update fulfillment order for now.
      // {
      //   label: 'Update Fulfillment Order',
      //   icon: '',
      //   onRowClick: (row) => {
      //     if (row && row.id) {
      //       this.updateFulfillmentOrder(row.id, row.salesOrderId);
      //     }
      //   },
      // },
      {
        label: 'Update Status Fulfillment Order',
        icon: '',
        onRowClick: (row) => {
          if (row && row.id) {
            this.updateStatusFulfillmentOrder(row.id);
          }
        },
      },
      {
        label: 'View Fulfillment Order',
        icon: '',
        onRowClick: (row) => {
          if (row && row.id) {
            this.viewFulfillmentOrder(row.id);
          }
        },
      },
      {
        label: 'Delete Fulfillment Order',
        icon: '',
        onRowClick: (row) => {
          if (row && row.id) {
            this.deleteFulfillmentOrder(row);
          }
        },
      },
      {
        label: 'Create Shipment',
        icon: '',
        onRowClick: (row) => {
          this.createShipmentFromFulfillmentOrder(row.id);
        },
      },
      {
        label: 'Push to Fulfillment Channel',
        icon: '',
        featureFlags: ['red_DV-3334_push_unpush_fulfillment_channel'],
        visible: (row) => {
          return row.pushedAt ? false : true;
        },
        onRowClick: (row) => {
          if (row && row.id) {
            this.pushToFulfillmentChannel(row);
          }
        },
      },
      {
        label: 'Unpush from WMS',
        icon: '',
        featureFlags: ['red_DV-3334_push_unpush_fulfillment_channel'],
        visible: (row) => {
          return row.pushedAt ? true : false;
        },
        onRowClick: (row) => {
          if (row && row.id) {
            this.removeFromFulfillmentChannel(row);
          }
        },
      },
    ];

    return this.getFilteredDropdownOptions(defaultActions);
  }

  private createShipmentFromFulfillmentOrder(fulfillmentOrderId :number) :void {
    this.dialog.open(OrderShipmentsFormComponent, {
      width: DialogConfig.Size.LARGE,
      data: {
        source: EShipmentFormDataSource.FULFILLMENT_ORDER,
        sourceId: fulfillmentOrderId,
      },
    });
  }

  triggerClearGridState() :void {
    this.clearGridStateTrigger$.next();
  }

  createFulfillmentOrder(salesOrderId :number) :void {
    this.loadingDialogService.show('Loading...');
    this.orderPostedApiService
      .getById({
        id: salesOrderId ?? 0,
        params: {
          relations: this.salesOrderRelations,
          relationsWithColumns: 'true',
          excludeIgnoredOrderItems: 'true',
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (salesOrder) => {
          if (salesOrder) {
            this.loadingDialogService.close();
            const salesOrderData :any[] = [];
            salesOrderData.push(salesOrder);

            if (this.ftPartialFulfillment) {
              const totalSalesOrderShipmentItemQuantity =
                this.decimalPipe.transform(
                  salesOrder.totalSalesOrderShipmentItemQuantity,
                );
              const totalSalesOrderItemQuantity = this.decimalPipe.transform(
                salesOrder.totalSalesOrderItemQuantity,
              );

              if (
                totalSalesOrderShipmentItemQuantity === totalSalesOrderItemQuantity
              ) {
                this.snackBar.open(
                  'Can not create fulfillment order with no remaining quantity to fulfill.',
                  'Fail',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );

                return;
              }
              this.dialog
                .open(OrderShipFormV2Component, {
                  data: {
                    title: 'Fulfill Orders',
                    salesOrders: salesOrderData,
                    ftEdiIntegrationEnabled: this.ftEdiIntegration,
                    isCreateFulfillmentOrder: true,
                  },
                  width: DialogConfig.sizes['xx-large'],
                })
                .afterClosed()
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((response :boolean) => {
                  this.refreshGrid();
                });

              return;
            }
            const modalRef = this.modal.open(ShipOrdersComponent, {
              size: 'lg',
              windowClass: 'ship-orders-modal',
            });
            modalRef.componentInstance.salesOrders = salesOrderData;
            modalRef.result.then(
              (hasUpdates :boolean) => {
                if (hasUpdates) {
                  this.refreshGrid();
                }
              },
              (_) => {},
            );
          }
        },
        error: (err) => {
          this.loadingDialogService.close();
          this.snackBar.open('Cannot create fulfillment order.', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private updateFulfillmentOrder(
    fulfillmentOrderId :number,
    salesOrderId :number,
  ) :void {
    this.loadingDialogService.show('Loading...');

    const fulfillmentOrderRequest = this.fulfillmentOrdersApiService.getById({
      id: fulfillmentOrderId ?? 0,
      params: {
        relations: ['shipmentItems'].join('|'),
      },
    });

    const salesOrderRequest = this.orderPostedApiService.getById({
      id: salesOrderId ?? 0,
      params: {
        relations: this.salesOrderRelations,
        relationsWithColumns: 'true',
        excludeIgnoredOrderItems: 'true',
      },
    });

    forkJoin([fulfillmentOrderRequest, salesOrderRequest])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: ([fulfillmentOrder, salesOrder]) => {
          this.loadingDialogService.close();

          if (fulfillmentOrder && salesOrder) {
            const salesOrderData :any[] = [salesOrder];

            if (this.ftPartialFulfillment) {
              this.dialog
                .open(OrderShipFormV2Component, {
                  data: {
                    title: 'Fulfill Orders',
                    salesOrders: salesOrderData,
                    ftEdiIntegrationEnabled: this.ftEdiIntegration,
                    fulfillmentOrder: fulfillmentOrder,
                    isCreateFulfillmentOrder: false,
                  },
                  width: DialogConfig.sizes['xx-large'],
                })
                .afterClosed()
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((response :boolean) => {
                  this.refreshGrid();
                });

              return;
            }

            const modalRef = this.modal.open(ShipOrdersComponent, {
              size: 'lg',
              windowClass: 'ship-orders-modal',
            });

            modalRef.componentInstance.salesOrders = salesOrderData;
            modalRef.result.then(
              (hasUpdates :boolean) => {
                if (hasUpdates) {
                  this.refreshGrid();
                }
              },
              (_) => {},
            );
          }
        },
        error: (err) => {
          this.loadingDialogService.close();
          this.snackBar.open('Cannot update fulfillment order.', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  private viewFulfillmentOrder(fulfillmentOrderId :number) :void {
    this.router.navigate([`/shipping/fulfillment-order/${fulfillmentOrderId}`]);
  }

  private updateStatusFulfillmentOrder(fulfillmentOrderId :number) :void {
    this.dialog
      .open(OrderFulfillmentStatusUpdateComponent, {
        data: {
          title: 'Update Order Status',
          orderStatusLookups: Object.values(EFulfillmentOrderStatus),
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result :any) => {
        const { orderStatus } = result;

        if (orderStatus) {
          this.fulfillmentOrdersApiService
            .updateStatus(fulfillmentOrderId, {
              orderStatus: orderStatus,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: () => {
                this.snackBar.open('Record status updated', 'Close', {
                  duration: 3000,
                });
                this.refreshGrid();
              },
              error: (e) => {
                this.snackBar.open(
                  'Cannot update the status of this record.',
                  'Close',
                  {
                    duration: 3000,
                  },
                );
              },
            });
        }
      });
  }

  private pushToFulfillmentChannel(fulfillmentOrder :any) :void {
    if (this.featureFlagUtilService.isFeatureEnabled('red_DV-3334_push_unpush_fulfillment_channel') && 
        fulfillmentOrder?.sourceReferenceIdentifier) {
      this.snackBar.open('Cannot push to fulfillment channel for fulfillment order that has been already pushed.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      return;
    }

    const selectedFulfillmentConfirmationValues = [
      {
        orderId: fulfillmentOrder.id || '',
        fulfillmentChannelName: fulfillmentOrder?.fulfillmentChannel || '',
        warehouseGroupName: fulfillmentOrder?.fulfillmentWarehouseGroup || '',
        fulfillmentChannelStoreName:
          fulfillmentOrder?.fulfillmentChannelStore || '',
      },
    ];

    this.dialog
      .open(UiFulfillmentActionConfirmationDialogComponent, {
        data: {
          title: 'Confirm Push to Fulfillment Channel',
          message:
            'Are you sure you want to push the selected orders to the respective channel?',
          fulfillmentConfirmationValues: selectedFulfillmentConfirmationValues,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Pushing fulfillment...');

          this.fulfillmentOrdersApiService
            .processFulfillmentOrderChannelAction({
              fulfillmentOrderIds: [fulfillmentOrder.id],
              action: EFulfillmentOrderAction.PUSH_TO_FULFILLMENT_CHANNEL,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response :any) => {
                this.loadingDialogService.close();

                const successIds = response.data?.successFulfillmentOrderIds || [];
                const failed = response.data?.failed || [];

                const successCount = successIds.length;
                const failCount = failed.length;

                if (successCount > 0) {
                  this.snackBar.open(
                    `${successCount} fulfillment order(s) pushed successfully.`,
                    'Success',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }

                if (failCount > 0) {
                  const failedMessagesArr = failed.map(function (fail) {
                    return `ID ${fail.fulfillmentOrderId}: ${fail.message}`;
                  });

                  const failedMessages = failedMessagesArr.join(', ');
                  this.snackBar.open(
                    `${failCount} failed to push: ${failedMessages}`,
                    'Fail',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }
                this.refreshGrid();
              },
              error: (response :any) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.refreshGrid();
              },
            });
        }
      });
  }

  private removeFromFulfillmentChannel(fulfillmentOrder :any) :void {
    const selectedFulfillmentConfirmationValues = [
      {
        orderId: fulfillmentOrder.id || '',
        fulfillmentChannelName: fulfillmentOrder?.fulfillmentChannel || '',
        warehouseGroupName: fulfillmentOrder?.fulfillmentWarehouseGroup || '',
        fulfillmentChannelStoreName:
          fulfillmentOrder?.fulfillmentChannelStore || '',
      },
    ];

    this.dialog
      .open(UiFulfillmentActionConfirmationDialogComponent, {
        data: {
          title: 'Confirm Unpush from WMS',
          message:
            'Are you sure you want to remove the selected orders from the respective channel?',
          fulfillmentConfirmationValues: selectedFulfillmentConfirmationValues,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Removing fulfillment...');
          this.fulfillmentOrdersApiService
            .processFulfillmentOrderChannelAction({
              fulfillmentOrderIds: [fulfillmentOrder.id],
              action: EFulfillmentOrderAction.REMOVE_FROM_FULFILLMENT_CHANNEL,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response :any) => {
                this.loadingDialogService.close();

                const successIds = response.data?.successFulfillmentOrderIds || [];
                const failed = response.data?.failed || [];

                const successCount = successIds.length;
                const failCount = failed.length;

                if (successCount > 0) {
                  this.snackBar.open(
                    `${successCount} fulfillment order(s) removed successfully.`,
                    'Success',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }

                if (failCount > 0) {
                  const failedMessagesArr = failed.map(function (fail) {
                    return `ID ${fail.fulfillmentOrderId}: ${fail.message}`;
                  });

                  const failedMessages = failedMessagesArr.join(', ');
                  this.snackBar.open(
                    `${failCount} failed to remove: ${failedMessages}`,
                    'Fail',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }
                this.refreshGrid();
              },
              error: (response :any) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.refreshGrid();
              },
            });
        }
      });
  }

  private deleteFulfillmentOrder(fulfillmentOrder :IFulfillmentOrder) :void {
    const hasShipment = fulfillmentOrder.hasShipment;
    const message = hasShipment
    ? 'This will delete the fulfillment order and its associated shipments. Are you sure?'
    : 'This will delete the fulfillment order. Are you sure?';

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message,
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.fulfillmentOrdersApiService.delete(fulfillmentOrder.id).subscribe({
            next: () => {
              this.snackBar.open('Record deleted', 'Close', {
                duration: 3000,
              });
              this.refreshGrid();
            },
            error: (e) => {
              this.snackBar.open('Cannot delete this record.', 'Close', {
                duration: 3000,
              });
              this.refreshGrid();
            },
          });
        }
      });
  }

  get ftPartialFulfillment() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-2840_partial_fulfillment',
    );
  }

  get ftEdiIntegration() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-2272_edi_integration',
    );
  }
}
